// Entry point. Assembles the four namespaces into a single Backlit
// object, then attaches it to window in the bundle footer (esbuild
// adds `window.backlit = backlit` after the IIFE).
//
// No guard against double-load: if a page accidentally <script src>s
// the bundle twice, the second IIFE rebinds window.backlit with an
// equivalent surface. No internal state to corrupt. A "smart" guard
// would break legitimate hot-reload flows.

import { createAuth, type AuthNamespace } from "./auth.js";
import { createData, type DataNamespace } from "./data.js";
import { createUserData, type UserDataNamespace } from "./userdata.js";
import { createCapture, type CaptureNamespace } from "./capture.js";
import { createRecords, type RecordsNamespace } from "./records.js";
import { createPush, type BacklitPush } from "./push.js";
import {
  createOffline,
  initOfflineApp,
  type OfflineNamespace,
  type OfflineOptions,
  type OfflineStatus,
  type OfflineEvent,
  type OfflineEventNext,
  type OfflineEventType,
  type OfflineEventOf,
  type OfflineConflict,
  type OfflineResolution,
  type OfflineValue,
  type PendingWrite,
  type OfflineSyncResult,
  type DeviceEntry,
  type RecordDeviceEntry,
  type DevicePage,
  type OfflinePin,
  type OfflinePrefetchOptions,
  type OfflinePrefetchProgress,
  type OfflinePrefetchResult,
} from "./offline.js";
import { createDevice, type DeviceNamespace } from "./device.js";
import { initFAB, removeFAB, configureFAB } from "./fab.js";
import { initHeartbeat } from "./heartbeat.js";
import {
  checkVersion as checkAppVersion,
  initAppVersion,
  onAppUpdateAvailable,
  type AppVersion,
  type AppUpdateAvailableEvent,
  type AppUpdateHandler,
} from "./app-version.js";
import { initServiceWorker } from "./sw-register.js";
import { initInstallPrompt } from "./install-prompt.js";
import { BacklitError, type BacklitErrorCode } from "./errors.js";
import {
  getBackend,
  type BacklitUser,
  type ListEntry,
  type ListOptions,
  type ListPage,
  type BatchEntry,
  type RecordBatchEntry,
  type GetBatchOptions,
  type CaptureListEntry,
  type Permission,
  type PutIfMatchResult,
  type StoredValue,
  type RecordResult,
  type RecordListEntry,
  type RecordPutIfMatchResult,
  type StorageChangeEvent,
  type StorageChangeHandler,
  type Unsubscribe,
} from "./backend.js";
import type {
  JSONResult,
  BatchJSONEntry,
  RecordBatchJSONEntry,
  PutJSONIfMatchResult,
  RecordJSONResult,
  RecordPutJSONIfMatchResult,
  UpdateJSONOptions,
} from "./json.js";
import { VERSION } from "./version.js";

export interface Backlit {
  readonly version: string;
  readonly auth: AuthNamespace;
  readonly data: DataNamespace;
  readonly userdata: UserDataNamespace;
  readonly capture: CaptureNamespace;
  readonly records: RecordsNamespace;
  // push enrolls the signed-in user's browser for Web Push notifications and
  // reports client-side push state. supported() is a sync capability probe (the
  // only sync, never-throwing SDK method); subscribe/state/unsubscribe/test are
  // async and throw BacklitError; watch() observes notification taps,
  // subscription rotations, and permission changes. The platform service worker
  // is auto-registered on load for every glow (install is universal, §8/D1),
  // independent of push.
  readonly push: BacklitPush;
  // offline opts in to working without a network: after `await
  // offline.enable()`, data/userdata/records reads fall back to an on-device
  // copy of server-acknowledged values and writes queue until the connection
  // returns. Nothing is stored on the device until enable() is called. Calls
  // made while enable() runs wait for it (at most 3 s); offline.whenReady()
  // resolves once it has settled.
  readonly offline: OfflineNamespace;
  // device is a small store for device-local preferences (e.g. a theme), never
  // app data: kept on this device only, never synced, tightly capped, and
  // cleared when a different user signs in or on logout. Works without
  // offline.enable(); nothing is opened until the first call.
  readonly device: DeviceNamespace;
  readonly BacklitError: typeof BacklitError;
  // on subscribes to runtime events. Today the only event is
  // "storage.change", fired whenever any data.*, userdata.*, or records.*
  // key is set or deleted on this glow. The handler receives a frozen
  // StorageChangeEvent; userdata events are scoped server-side to the
  // writing user's verified email, so peer tabs signed in as another
  // user never see them. data.* and records.* events are unscoped (every
  // session of the glow); records events carry the owner uid.
  //
  // Returns an unsubscribe function. Idempotent — calling it twice is
  // a no-op. The same handler may be registered multiple times; each
  // registration returns its own unsubscribe.
  //
  // The SDK opens a WebSocket to /_backlit/events on first subscribe;
  // reconnects with exponential backoff on transient drops, but parks the
  // loop when a session probe says the session has ended, until a heartbeat
  // succeeds (events.ts); closes the socket 5s after the last unsubscribe.
  //
  // Delivery is best-effort: events that arrive during a reconnect
  // window are dropped. Callers that need exact reconciliation can
  // re-list() after their first event.
  //
  // "app-update-available" fires when the glow's live version differs from the
  // one this page was loaded from (app-version.ts): automatically on an
  // offline.json glow, and from checkVersion() on any glow. A handler added
  // while an update is outstanding receives it once, asynchronously.
  on(event: "storage.change", handler: StorageChangeHandler): Unsubscribe;
  on(event: "app-update-available", handler: AppUpdateHandler): Unsubscribe;

  // checkVersion resolves { client, server }: the version this page was loaded
  // from and the glow's live version (one unmetered heartbeat). server is null
  // with no answer (offline) or a non-2xx; client is null when unknown. When
  // both are known and differ it also fires "app-update-available".
  checkVersion(): Promise<AppVersion>;

  // hideUI removes the Backlit floating action button this SDK injects on load.
  // It is honoured only on a paid plan (a free author's call is a silent no-op);
  // it never throws and is idempotent. Removing branding is a paid feature
  // (CLAUDE.md §8). Race-safe: calling it before the FAB has injected still
  // suppresses it for an entitled author.
  hideUI(): void;
}

const backlit: Backlit = {
  version: VERSION,
  auth: createAuth(),
  data: createData(),
  userdata: createUserData(),
  capture: createCapture(),
  records: createRecords(),
  push: createPush(),
  offline: createOffline(),
  device: createDevice(),
  BacklitError,
  on(event: string, handler: StorageChangeHandler | AppUpdateHandler): Unsubscribe {
    if (event === "app-update-available") {
      return onAppUpdateAvailable(handler as AppUpdateHandler);
    }
    if (event !== "storage.change") {
      throw new BacklitError(
        "invalid_argument",
        `unknown event ${JSON.stringify(event)}; supported events are "storage.change" and "app-update-available"`,
      );
    }
    return getBackend().onStorageChange(handler as StorageChangeHandler);
  },
  checkVersion: checkAppVersion,
  hideUI() {
    removeFAB();
  },
};

// Wire the FAB's login/logout buttons to the in-scope auth namespace (NOT
// window.backlit, which the bundle footer binds only after this IIFE), then
// auto-inject the FAB. initFAB() guards non-browser / off-host internally, so
// this is a no-op outside a deployed glow.
configureFAB(
  (returnPath) => backlit.auth.login(returnPath),
  (returnPath) => backlit.auth.logout(returnPath),
);
initFAB();

// Learn the version this page was loaded from and watch heartbeats for a newer
// live one ("app-update-available"). Before initHeartbeat so the load beat is
// observed. Guards non-browser / off-host internally.
initAppVersion();

// Keep an open page's session alive: fire GET /_backlit/heartbeat on load
// (if visible) and then hourly. initHeartbeat() guards non-browser / off-host
// internally, so this is a no-op outside a deployed glow.
initHeartbeat();

// Universal PWA install (§8/D1): auto-register the platform service worker
// (/_backlit/sw.js, root scope) on load for EVERY glow, best-effort/silent and
// independent of push. initServiceWorker() guards non-browser / no-SW /
// off-host internally, so this is a no-op outside a deployed glow.
initServiceWorker();

// backlit.offline's app state: listen for the service worker's offline
// messages from load, so an app-ready posted before the app calls watch() is
// not lost. Never opens on-device storage (that waits for offline.enable()).
// Guards non-browser / no-SW / off-host internally.
initOfflineApp();

// Suppress Chromium's automatic PWA install prompt (§8): a glow stays
// installable (address-bar icon / menu), but the SDK preventDefault()s
// `beforeinstallprompt` so the browser doesn't proactively nag. Safari/Firefox
// never fire it, so this is Chromium-only. Guards non-browser / off-host
// internally — a no-op outside a deployed glow.
initInstallPrompt();

// Re-exported as the IIFE's named exports so esbuild's globalName binds a
// shape with these props. The bundle footer copies the IIFE's return
// value onto window.backlit.
export const version = backlit.version;
export const auth = backlit.auth;
export const data = backlit.data;
export const userdata = backlit.userdata;
export const capture = backlit.capture;
export const records = backlit.records;
export const push = backlit.push;
export const offline = backlit.offline;
export const device = backlit.device;
export const on = backlit.on;
export const checkVersion = backlit.checkVersion;
export const hideUI = backlit.hideUI;
export { BacklitError };
export type { RecordsNamespace };
export type { BacklitPush };
export type { DeviceNamespace };
export type { OfflineNamespace, OfflineOptions, OfflineStatus, OfflineEvent, OfflineEventNext, OfflineEventType, OfflineEventOf, OfflineConflict, OfflineResolution, OfflineValue, PendingWrite, OfflineSyncResult, DeviceEntry, RecordDeviceEntry, DevicePage, OfflinePin, OfflinePrefetchOptions, OfflinePrefetchProgress, OfflinePrefetchResult };
export type { AppVersion, AppUpdateAvailableEvent, AppUpdateHandler };
export type { BacklitErrorCode, BacklitUser, ListEntry, ListOptions, ListPage, BatchEntry, RecordBatchEntry, GetBatchOptions, CaptureListEntry, Permission, PutIfMatchResult, StoredValue, RecordResult, RecordListEntry, RecordPutIfMatchResult, JSONResult, BatchJSONEntry, RecordBatchJSONEntry, PutJSONIfMatchResult, RecordJSONResult, RecordPutJSONIfMatchResult, UpdateJSONOptions, StorageChangeEvent, StorageChangeHandler, Unsubscribe };
